import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { post } from "../services/api";
import type { Generation } from "../types";
type QueueValue = {
  items: Generation[];
  track(item: Generation): void;
  dismiss(id: string): void;
};
const Context = createContext<QueueValue | null>(null);
const KEY = "lumora:render-queue:v1";
export function RenderQueueProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<Generation[]>(() => {
    try {
      return JSON.parse(sessionStorage.getItem(KEY) ?? "[]");
    } catch {
      return [];
    }
  });
  useEffect(() => {
    sessionStorage.setItem(KEY, JSON.stringify(items.slice(0, 20)));
  }, [items]);
  useEffect(() => {
    const active = items.filter(
      (x) => !["completed", "failed", "timed_out"].includes(x.status),
    );
    if (!active.length) return;
    const timer = setTimeout(() => {
      void Promise.all(
        active.map(async (item) => {
          try {
            const updated = await post<Generation>(
              `/api/generations/${item.id}/refresh`,
              {},
            );
            setItems((current) =>
              current.map((x) => (x.id === item.id ? updated : x)),
            );
          } catch {}
        }),
      );
    }, 5000);
    return () => clearTimeout(timer);
  }, [items]);
  const track = useCallback(
      (item: Generation) =>
        setItems((current) =>
          [item, ...current.filter((x) => x.id !== item.id)].slice(0, 20),
        ),
      [],
    ),
    dismiss = useCallback(
      (id: string) => setItems((c) => c.filter((x) => x.id !== id)),
      [],
    );
  const value = useMemo(
    () => ({ items, track, dismiss }),
    [items, track, dismiss],
  );
  return <Context.Provider value={value}>{children}</Context.Provider>;
}
export function useRenderQueue() {
  const v = useContext(Context);
  if (!v) throw new Error("RenderQueueProvider missing");
  return v;
}
